import React, { useEffect, useRef } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { cn } from "../../lib/utils";

// id usado pelo App para descontar a altura da barra fixa ao rolar até os produtos
export const CATALOG_TOOLBAR_ID = "catalog-toolbar";

/**
 * Barra "sticky" de busca + trilho horizontal de categorias (layout docs/tela).
 *
 * @param {string} searchTerm - Termo de busca atual
 * @param {function} setSearchTerm - Atualiza o termo de busca
 * @param {string} selectedCategory - ID da categoria selecionada ("" = todas)
 * @param {function} setSelectedCategory - Seleciona a categoria
 * @param {Array} categories - Categorias disponíveis ({ id, name, icon })
 */
export const SearchAndFilters = ({
  searchTerm = "",
  setSearchTerm = () => {},
  selectedCategory = "",
  setSelectedCategory = () => {},
  categories = [],
}) => {
  const railRef = useRef(null);

  // Mantém a pílula ativa visível no trilho (útil no celular, quando a categoria muda pelo select)
  useEffect(() => {
    const rail = railRef.current;
    const active = rail?.querySelector('[aria-pressed="true"]');
    if (!rail || !active) return;
    const left = active.offsetLeft - rail.clientWidth / 2 + active.clientWidth / 2;
    rail.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [selectedCategory, categories.length]);

  const pills = [{ id: "", name: "Todos", icon: "✨" }, ...categories];

  return (
    <div
      id={CATALOG_TOOLBAR_ID}
      className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-cocoa/10 shadow-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-2 space-y-2.5">
        <div className="flex items-center gap-3">
          {/* Busca */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-cocoa-muted pointer-events-none" />
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              aria-label="Buscar produtos"
              placeholder="Buscar no cardápio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-10 pr-11 bg-white border border-cocoa/10 text-cocoa rounded-xl text-base sm:text-sm placeholder:text-cocoa-muted/70 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 [&::-webkit-search-cancel-button]:hidden"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                aria-label="Limpar busca"
                className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-cocoa-muted hover:text-cocoa hover:bg-cream-100"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filtro rápido (tablet/desktop) */}
          <label className="hidden md:flex items-center gap-2 flex-shrink-0 text-xs text-cocoa-muted font-medium">
            <SlidersHorizontal className="w-4 h-4 text-brand-600" />
            <span>Filtrar:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-11 bg-white border border-cocoa/10 text-sm font-semibold rounded-xl pl-3 pr-8 text-cocoa shadow-sm max-w-[16rem] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">Todo o cardápio</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Trilho de categorias */}
        <nav
          aria-label="Categorias"
          className="-mx-4 sm:-mx-6 lg:-mx-8 [mask-image:linear-gradient(to_right,transparent,#000_1rem,#000_calc(100%-1.5rem),transparent)]"
        >
          <div
            ref={railRef}
            className="flex items-center gap-2 overflow-x-auto hide-scrollbar px-4 sm:px-6 lg:px-8 py-1"
          >
            {pills.map((category) => {
              const active = String(selectedCategory) === String(category.id);
              return (
                <button
                  key={category.id || "todos"}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelectedCategory(category.id)}
                  className={cn(
                    "flex-shrink-0 inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-[38px] px-4 rounded-full text-[13px] whitespace-nowrap transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
                    active
                      ? "bg-brand-500 text-white font-bold shadow-md shadow-brand-500/30"
                      : "bg-white text-cocoa font-semibold border border-cocoa/10 hover:border-brand-300 hover:bg-brand-50"
                  )}
                >
                  {category.icon && <span aria-hidden="true">{category.icon}</span>}
                  {category.name}
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
};
